import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Activity, Music2, Gauge, Radio, ChevronLeft, ChevronRight } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  YAxis,
  Tooltip,
} from "recharts";
import { useTempoDetector, SUBDIVISIONS } from "../hooks/useTempoDetector";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";

const NOTE_OPTIONS = [3, 4, 8];
const FOAM_TEXTURE =
  "https://images.unsplash.com/photo-1640906152676-dace6710d24b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWNvdXN0aWMlMjBmb2FtJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODUxODY1ODF8MA&ixlib=rb-4.1.0&q=85";

function useBeat(bpm, isListening) {
  const [beat, setBeat] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!isListening || !bpm || bpm <= 0) {
      setBeat(0);
      return;
    }
    const interval = 60000 / bpm;
    timerRef.current = setInterval(() => {
      setBeat((b) => (b + 1) % 4);
    }, interval);
    return () => clearInterval(timerRef.current);
  }, [bpm, isListening]);
  return beat;
}

const ZOOM_LEVELS = [
  { label: "4s", len: 260 },
  { label: "10s", len: 600 },
  { label: "30s", len: 1800 },
  { label: "All", len: 999999 },
];

function AttackMonitor({ monitorRef, isListening }) {
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const offsetRef = useRef(0);
  const viewLenRef = useRef(260);
  const followRef = useRef(true);
  const [zoom, setZoom] = useState(260);
  const [following, setFollowing] = useState(true);

  // When (re)starting a session, snap back to live-follow.
  useEffect(() => {
    if (isListening) {
      followRef.current = true;
      setFollowing(true);
    }
  }, [isListening]);

  useEffect(() => {
    let raf;
    const draw = () => {
      const canvas = canvasRef.current;
      const data = monitorRef.current || [];
      const total = data.length;
      const viewLen = Math.min(viewLenRef.current, Math.max(1, total));
      const maxOffset = Math.max(0, total - viewLen);

      if (followRef.current) offsetRef.current = maxOffset;
      else offsetRef.current = Math.min(offsetRef.current, maxOffset);
      const start = Math.floor(offsetRef.current);
      const view = data.slice(start, start + viewLen);

      if (scrollRef.current) {
        scrollRef.current.max = String(maxOffset);
        scrollRef.current.value = String(start);
      }

      if (canvas) {
        const ctx = canvas.getContext("2d");
        const w = canvas.width;
        const h = canvas.height;
        ctx.clearRect(0, 0, w, h);

        ctx.strokeStyle = "rgba(255,255,255,0.04)";
        ctx.lineWidth = 1;
        for (let gy = 1; gy < 4; gy++) {
          const y = (h / 4) * gy;
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(w, y);
          ctx.stroke();
        }

        if (view.length > 1) {
          // Energy uses its own scale; flux + its threshold share a second scale
          // (they are tiny compared to energy) so both are readable.
          let eMax = 0.08;
          let fMax = 0.02;
          for (const d of view) {
            if (d.rms > eMax) eMax = d.rms;
            if (d.flux > fMax) fMax = d.flux;
            if (d.fluxThr > fMax) fMax = d.fluxThr;
          }
          eMax *= 1.15;
          fMax *= 1.25;
          const n = view.length;
          const isAll = viewLenRef.current >= 999999;
          const spanForX = isAll ? Math.max(2, n) : viewLenRef.current;
          const xAt = (i) => (i / (spanForX - 1)) * w;
          const yEnergy = (v) => h - Math.min(1, v / eMax) * h;
          const yFlux = (v) => h - Math.min(1, v / fMax) * h;

          // onset markers
          ctx.strokeStyle = "rgba(255,59,48,0.75)";
          ctx.lineWidth = 2;
          view.forEach((d, i) => {
            if (d.onset) {
              const x = xAt(i);
              ctx.beginPath();
              ctx.moveTo(x, 0);
              ctx.lineTo(x, h);
              ctx.stroke();
            }
          });

          // energy area + line (green)
          ctx.beginPath();
          ctx.moveTo(0, h);
          view.forEach((d, i) => ctx.lineTo(xAt(i), yEnergy(d.rms)));
          ctx.lineTo(w, h);
          ctx.closePath();
          ctx.fillStyle = "rgba(0,255,102,0.10)";
          ctx.fill();
          ctx.strokeStyle = "#00FF66";
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          view.forEach((d, i) => {
            const x = xAt(i);
            const y = yEnergy(d.rms);
            i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          });
          ctx.stroke();

          // flux threshold (amber dashed)
          ctx.strokeStyle = "#FFB020";
          ctx.lineWidth = 1.5;
          ctx.setLineDash([5, 4]);
          ctx.beginPath();
          view.forEach((d, i) => {
            const x = xAt(i);
            const y = yFlux(d.fluxThr);
            i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          });
          ctx.stroke();
          ctx.setLineDash([]);

          // flux / rise (cyan)
          ctx.strokeStyle = "#38BDF8";
          ctx.lineWidth = 1.75;
          ctx.beginPath();
          view.forEach((d, i) => {
            const x = xAt(i);
            const y = yFlux(d.flux);
            i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
          });
          ctx.stroke();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [monitorRef]);

  const onScroll = (e) => {
    offsetRef.current = Number(e.target.value);
    followRef.current = false;
    setFollowing(false);
  };

  const goLive = () => {
    followRef.current = true;
    setFollowing(true);
  };

  const applyZoom = (len) => {
    viewLenRef.current = len;
    setZoom(len);
  };

  return (
    <div>
      <div className="border border-neutral-800 bg-[#0A0A0A]">
        <canvas
          ref={canvasRef}
          width={1120}
          height={200}
          className="h-[200px] w-full"
          data-testid="attack-monitor-canvas"
        />
      </div>

      <input
        ref={scrollRef}
        type="range"
        min={0}
        max={0}
        defaultValue={0}
        onInput={onScroll}
        onMouseDown={() => {
          followRef.current = false;
          setFollowing(false);
        }}
        onTouchStart={() => {
          followRef.current = false;
          setFollowing(false);
        }}
        data-testid="attack-monitor-scrollbar"
        className="mt-3 w-full cursor-pointer accent-[#00FF66]"
      />

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2" data-testid="monitor-zoom">
          {ZOOM_LEVELS.map((z) => (
            <button
              key={z.label}
              onClick={() => applyZoom(z.len)}
              data-testid={`zoom-${z.label}`}
              className={`h-8 min-w-[48px] border px-2 text-xs font-semibold transition-colors duration-200 ${
                zoom === z.len
                  ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                  : "border-neutral-800 bg-[#141414] text-neutral-400 hover:border-neutral-600"
              }`}
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {z.label}
            </button>
          ))}
        </div>
        <button
          onClick={goLive}
          data-testid="monitor-live-button"
          className={`h-8 border px-4 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${
            following
              ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]"
              : "border-neutral-800 bg-[#141414] text-neutral-400 hover:border-[#FF3B30]/60"
          }`}
        >
          {following ? "● Live" : "Jump to Live"}
        </button>
      </div>
    </div>
  );
}

export default function TempoDetector() {
  const {
    isListening,
    bpm,
    avgBpm,
    pitch,
    level,
    onsetCount,
    history,
    error,
    noteCount,
    setNoteCount,
    subdivision,
    setSubdivision,
    inputGain,
    setInputGain,
    autoGain,
    setAutoGain,
    signalStatus,
    onsetFactor,
    setOnsetFactor,
    noiseGate,
    setNoiseGate,
    monitorRef,
    start,
    stop,
  } = useTempoDetector();

  const beat = useBeat(bpm, isListening);
  const levelPct = Math.min(100, Math.round(level * 600));

  const SIGNAL = {
    idle: { label: "—", color: "#525252" },
    low: { label: "TOO QUIET", color: "#FFB020" },
    good: { label: "GOOD", color: "#00FF66" },
    clip: { label: "CLIPPING", color: "#FF3B30" },
  };
  const sig = SIGNAL[signalStatus] || SIGNAL.idle;

  const [step, setStep] = useState(1);

  const settingsPanel = (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="settings-panel">
      <div className="border border-neutral-800 bg-[#141414] p-6">
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">
          Notes used for tempo
        </span>
        <p className="mt-1 text-sm text-neutral-400">
          Analysis window — more = steadier reading, fewer = reacts faster.
        </p>
        <div className="mt-4 flex items-center gap-2" data-testid="note-count-select">
          {NOTE_OPTIONS.map((n) => (
            <button
              key={n}
              onClick={() => setNoteCount(n)}
              data-testid={`note-count-${n}`}
              className={`h-11 w-16 border text-lg font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00FF66] focus:ring-offset-2 focus:ring-offset-[#0A0A0A] ${
                noteCount === n
                  ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                  : "border-neutral-800 bg-[#0A0A0A] text-neutral-400 hover:border-neutral-600"
              }`}
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-neutral-800 bg-[#141414] p-6">
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">
          Note value you're playing
        </span>
        <p className="mt-1 text-sm text-neutral-400">
          Tells the counter each attack is a{" "}
          {subdivision === "1/8T" ? "triplet" : subdivision} note, so BPM scales
          to the quarter-note beat.
        </p>
        <div className="mt-4 flex items-center gap-2" data-testid="subdivision-select">
          {SUBDIVISIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSubdivision(s.id)}
              data-testid={`subdivision-${s.id}`}
              className={`h-11 flex-1 border text-base font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00FF66] focus:ring-offset-2 focus:ring-offset-[#0A0A0A] ${
                subdivision === s.id
                  ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                  : "border-neutral-800 bg-[#0A0A0A] text-neutral-400 hover:border-neutral-600"
              }`}
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const micButton = (
    <button
      onClick={isListening ? stop : start}
      data-testid="mic-toggle"
      className={`group relative flex min-h-[130px] w-full flex-col items-center justify-center gap-3 border p-6 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#0A0A0A] ${
        isListening
          ? "border-[#FF3B30] bg-[#FF3B30]/10 focus:ring-[#FF3B30]"
          : "border-neutral-800 bg-[#141414] hover:border-[#00FF66]/60 focus:ring-[#00FF66]"
      }`}
      style={isListening ? { boxShadow: "inset 0 0 30px rgba(255,59,48,0.25)" } : {}}
    >
      {isListening ? (
        <MicOff className="h-8 w-8 text-[#FF3B30]" />
      ) : (
        <Mic className="h-8 w-8 text-neutral-300 group-hover:text-[#00FF66] transition-colors duration-200" />
      )}
      <span className="text-sm font-semibold uppercase tracking-[0.2em]">
        {isListening ? "Stop" : "Start Listening"}
      </span>
    </button>
  );

  const pitchPanel = (
    <div className="border border-neutral-800 bg-[#141414] p-5" data-testid="pitch-display">
      <div className="flex items-center gap-2">
        <Music2 className="h-3.5 w-3.5 text-neutral-500" />
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">Pitch</span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span
          className={`text-4xl font-bold ${pitch ? "text-[#00FF66]" : "text-neutral-700"}`}
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          data-testid="pitch-note"
        >
          {pitch ? `${pitch.name}${pitch.octave}` : "--"}
        </span>
        {pitch && <span className="text-sm text-neutral-500">{pitch.frequency} Hz</span>}
      </div>
      {pitch && (
        <div className="mt-1 text-xs text-neutral-500">
          {pitch.cents > 0 ? "+" : ""}
          {pitch.cents} cents
        </div>
      )}
    </div>
  );

  const signalPanel = (
    <div className="border border-neutral-800 bg-[#141414] p-5" data-testid="signal-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">Signal</span>
        </div>
        <span
          className="text-xs font-semibold uppercase tracking-[0.2em]"
          style={{ color: sig.color }}
          data-testid="signal-status"
        >
          {sig.label}
        </span>
      </div>

      <div className="mt-3 h-2 w-full overflow-hidden bg-neutral-800">
        <div
          className="h-full transition-[width] duration-75"
          style={{ width: `${levelPct}%`, backgroundColor: sig.color }}
          data-testid="input-level-bar"
        />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.2em] text-neutral-400">Auto Gain</span>
        <Switch checked={autoGain} onCheckedChange={setAutoGain} data-testid="auto-gain-switch" />
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">Input Gain</span>
          <span
            className="text-xs text-neutral-400"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            data-testid="gain-value"
          >
            {inputGain.toFixed(2)}x
          </span>
        </div>
        <Slider
          value={[inputGain]}
          min={0.2}
          max={12}
          step={0.1}
          disabled={autoGain}
          onValueChange={(v) => setInputGain(v[0])}
          data-testid="gain-slider"
          className={autoGain ? "opacity-40" : ""}
        />
      </div>
    </div>
  );

  const attackMonitorPanel = (
    <div className="border border-neutral-800 bg-[#141414] p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-neutral-500" />
          <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">
            Attack Monitor
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-[0.15em]">
          <span className="flex items-center gap-1.5 text-neutral-400">
            <span className="inline-block h-2 w-3 bg-[#00FF66]" /> Energy
          </span>
          <span className="flex items-center gap-1.5 text-neutral-400">
            <span className="inline-block h-2 w-3 bg-[#38BDF8]" /> Rise
          </span>
          <span className="flex items-center gap-1.5 text-neutral-400">
            <span className="inline-block h-2 w-3 bg-[#FFB020]" /> Trigger
          </span>
          <span className="flex items-center gap-1.5 text-neutral-400">
            <span className="inline-block h-2 w-3 bg-[#FF3B30]" /> Onset
          </span>
        </div>
      </div>

      <div className="mt-4">
        <AttackMonitor monitorRef={monitorRef} isListening={isListening} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] text-neutral-400">
              Sensitivity (rise vs recent)
            </span>
            <span
              className="text-xs text-[#FFB020]"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              data-testid="onset-factor-value"
            >
              {onsetFactor.toFixed(1)}x
            </span>
          </div>
          <Slider
            value={[onsetFactor]}
            min={1.2}
            max={5}
            step={0.1}
            onValueChange={(v) => setOnsetFactor(v[0])}
            data-testid="onset-factor-slider"
          />
          <p className="mt-1 text-[11px] text-neutral-500">
            How far a note's rise (cyan) must exceed recent rise activity (amber).
            Lower = counts more/faster notes; raise if extra onsets appear.
          </p>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] text-neutral-400">
              Noise gate (min rise)
            </span>
            <span
              className="text-xs text-neutral-300"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              data-testid="noise-gate-value"
            >
              {noiseGate.toFixed(3)}
            </span>
          </div>
          <Slider
            value={[noiseGate]}
            min={0.002}
            max={0.05}
            step={0.002}
            onValueChange={(v) => setNoiseGate(v[0])}
            data-testid="noise-gate-slider"
          />
          <p className="mt-1 text-[11px] text-neutral-500">
            Smallest true (pre-gain) rise that counts as a note. Raise to ignore
            string/room noise; lower to catch very soft attacks.
          </p>
        </div>
      </div>
    </div>
  );

  const bpmHero = (
    <div
      className="relative flex flex-col justify-between overflow-hidden border border-neutral-800 bg-[#141414] p-8 min-h-[280px]"
      data-testid="bpm-display"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">Tempo</span>
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">
          {noteCount} × {subdivision} notes
        </span>
      </div>

      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="flex items-end gap-4">
          <div
            className="leading-none tracking-tighter transition-transform duration-100"
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              transform: isListening && beat === 0 ? "scale(1.04)" : "scale(1)",
              textShadow:
                isListening && bpm > 0 ? "0 0 40px rgba(0,255,102,0.35)" : "none",
            }}
          >
            <span
              className={`text-7xl md:text-9xl font-bold ${bpm > 0 ? "text-white" : "text-neutral-700"}`}
              data-testid="bpm-value"
            >
              {bpm > 0 ? bpm : "--"}
            </span>
          </div>
          <div className="mb-3 flex flex-col">
            <span
              className="text-xl font-medium text-[#00FF66]"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              BPM
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Live</span>
          </div>
        </div>

        <div
          className="flex flex-col items-start border-l border-neutral-800 pl-6 md:items-end md:pl-8"
          data-testid="avg-bpm-display"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Session Average
          </span>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              className={`text-4xl md:text-5xl font-bold ${avgBpm > 0 ? "text-[#00FF66]" : "text-neutral-700"}`}
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              data-testid="avg-bpm-value"
            >
              {avgBpm > 0 ? avgBpm : "--"}
            </span>
            <span className="text-sm text-neutral-500">BPM</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3" data-testid="metronome">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="h-3 w-3 rounded-full transition-colors duration-75"
            style={{
              backgroundColor: isListening && bpm > 0 && beat === i ? "#00FF66" : "#2a2a2a",
              boxShadow:
                isListening && bpm > 0 && beat === i ? "0 0 12px rgba(0,255,102,0.7)" : "none",
            }}
          />
        ))}
        <span className="ml-auto text-xs uppercase tracking-[0.2em] text-neutral-500">
          {onsetCount} notes detected
        </span>
      </div>
    </div>
  );

  const historyPanel = (
    <div className="border border-neutral-800 bg-[#141414] p-6">
      <div className="flex items-center gap-2">
        <Activity className="h-3.5 w-3.5 text-neutral-500" />
        <span className="text-xs uppercase tracking-[0.3em] text-neutral-500">Tempo History</span>
      </div>
      <div className="mt-4 h-56 w-full" data-testid="history-graph">
        {history.filter((h) => h.bpm > 0).length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={history.filter((h) => h.bpm > 0)}
              margin={{ top: 10, right: 8, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="bpmFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00FF66" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00FF66" stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis
                domain={["dataMin - 10", "dataMax + 10"]}
                tick={{ fill: "#666", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#141414",
                  border: "1px solid #262626",
                  borderRadius: 0,
                  color: "#fff",
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
                labelStyle={{ color: "#666" }}
                formatter={(v) => [`${v} BPM`, ""]}
                labelFormatter={(_, p) => (p && p[0] ? `Note ${p[0].payload.note}` : "")}
              />
              <Area
                type="monotone"
                dataKey="bpm"
                stroke="#00FF66"
                strokeWidth={2}
                fill="url(#bpmFill)"
                isAnimationActive={false}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-neutral-600">
            Start playing to plot your tempo over time.
          </div>
        )}
      </div>
    </div>
  );

  const STEPS = ["Settings", "Level & Detection", "Live"];
  const stepNav = (
    <div className="mt-6 flex items-center gap-2" data-testid="step-nav">
      {STEPS.map((label, i) => {
        const n = i + 1;
        return (
          <div key={label} className="flex items-center gap-2">
            <button
              onClick={() => setStep(n)}
              data-testid={`step-tab-${n}`}
              className={`flex items-center gap-2 border px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                step === n
                  ? "border-[#00FF66] bg-[#00FF66]/10 text-[#00FF66]"
                  : "border-neutral-800 bg-[#141414] text-neutral-500 hover:border-neutral-600"
              }`}
            >
              <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{n}</span>
              <span className="hidden sm:inline">{label}</span>
            </button>
            {n < STEPS.length && <span className="h-px w-4 bg-neutral-800" />}
          </div>
        );
      })}
    </div>
  );

  const navButtons = (backTo, nextTo, nextLabel = "Next") => (
    <div className="col-span-1 flex items-center justify-between md:col-span-6">
      {backTo ? (
        <button
          onClick={() => setStep(backTo)}
          data-testid="wizard-back"
          className="flex items-center gap-2 border border-neutral-800 bg-[#141414] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-300 transition-colors duration-200 hover:border-neutral-600"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
      ) : (
        <span />
      )}
      {nextTo && (
        <button
          onClick={() => setStep(nextTo)}
          data-testid="wizard-next"
          className="flex items-center gap-2 border border-[#00FF66] bg-[#00FF66]/10 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#00FF66] transition-colors duration-200 hover:bg-[#00FF66]/20"
        >
          {nextLabel} <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <div
      className="min-h-screen w-full bg-[#0A0A0A] text-white relative overflow-hidden"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {/* Background texture */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.06] mix-blend-overlay bg-cover bg-center"
        style={{ backgroundImage: `url(${FOAM_TEXTURE})` }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% -10%, rgba(0,255,102,0.05), transparent 55%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-12">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-neutral-800 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center border border-neutral-800 bg-[#141414]">
              <Gauge className="h-5 w-5 text-[#00FF66]" />
            </div>
            <div>
              <h1
                className="text-lg font-bold tracking-tight"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                data-testid="app-title"
              >
                BPM<span className="text-[#00FF66]"> Master</span>
              </h1>
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Live Guitar BPM Detector
              </p>
            </div>
          </div>
          <div
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
            data-testid="status-indicator"
          >
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                isListening
                  ? "bg-[#FF3B30] animate-pulse"
                  : "bg-neutral-600"
              }`}
            />
            <span className="text-neutral-400">
              {isListening ? "Listening" : "Idle"}
            </span>
          </div>
        </header>

        {error && (
          <div
            className="mt-6 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-3 text-sm text-[#FF6B60]"
            data-testid="error-message"
          >
            {error}
          </div>
        )}

        {stepNav}

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-6">
          {step === 1 && (
            <>
              <div className="col-span-1 md:col-span-6">{settingsPanel}</div>
              {navButtons(null, 2, "Next: Set Level")}
            </>
          )}

          {step === 2 && (
            <>
              <div className="col-span-1 text-sm text-neutral-400 md:col-span-6">
                Play the way you'll actually play. Nudge the gain until{" "}
                <span className="text-neutral-200">Signal</span> reads GOOD (not
                CLIPPING), then watch the Attack Monitor and adjust Sensitivity /
                Noise gate until a red onset lands on every note.
              </div>
              <div className="col-span-1 md:col-span-2">{micButton}</div>
              <div className="col-span-1 md:col-span-4">{signalPanel}</div>
              <div className="col-span-1 md:col-span-6">{attackMonitorPanel}</div>
              {navButtons(1, 3, "Next: Live View")}
            </>
          )}

          {step === 3 && (
            <>
              <div className="col-span-1 md:col-span-4">{bpmHero}</div>
              <div className="col-span-1 flex flex-col gap-4 md:col-span-2">
                {micButton}
                {pitchPanel}
                {signalPanel}
              </div>
              <div className="col-span-1 md:col-span-6">{attackMonitorPanel}</div>
              <div className="col-span-1 md:col-span-6">{historyPanel}</div>
              {navButtons(2, null)}
            </>
          )}
        </div>

        <footer className="mt-8 border-t border-neutral-800 pt-4 text-xs text-neutral-600">
          All audio is processed locally in your browser — nothing is uploaded.
          Best with a clean, plugged-in or close-mic'd guitar signal.
        </footer>
      </div>
    </div>
  );
}
